/* ──────────────────────────────────────────────────────────────────────
   Triple Y · בחירה אוטומטית של «מצב המכשיר». 21.09.2026

   הרקע: לאותו צירוף נפח וצבע יכולים להיות שני מק"טים, «חדש» (אטום)
   ו-«Active New» (עבר אקטיבציה). שניהם חייבים להתקיים בשופיפיי, אחרת
   סנכרון סיילספורס אינו מוצא לאן לכתוב את המלאי. אבל יובל **אינו רוצה
   להציע את שניהם ללקוח**, ולכן האופציה מוסתרת והקוד בוחר.

   הכלל, בהכרעת יובל 21.09.2026:
     ① ברירת מחדל: **«חדש» קודם.** אם אין ממנו מלאי בצירוף הנבחר,
        עוברים ל-«Active New».
     ② דריסה ידנית: מטא-שדה המוצר `custom.condition_mode`
          auto    ⟵ הכלל שלמעלה
          new     ⟵ «חדש» בלבד
          active  ⟵ «Active New» בלבד

   🔴 למה בכלל צריך קוד, ולא מספיקה ברירת המחדל של שופיפיי:
      ‏`first_available_variant` אכן מעדיפה את הערך הראשון באופציה, ולכן
      **בטעינת הדף** התוצאה כבר נכונה. היא אינה עוזרת אחרי שהלקוח מחליף
      נפח או צבע: אז הבורר שומר על ה«מצב» שנבחר, וייתכן שלצירוף החדש אין
      ממנו מלאי. אז הלקוח רואה «אזל» על מוצר שיש ממנו.

   🔴 לא נוגעים בטופס ולא שולחים בקשה בעצמנו. מקליקים על הרדיו המוסתר,
      ומשם המנגנון הרגיל של התמה ממשיך. כל מי שמאזין לשינוי ווריאנט
      ממשיך לעבוד בלי לדעת שהיינו כאן.
   ────────────────────────────────────────────────────────────────────── */
(() => {
  'use strict';

  /* 🔴 שם הדגל משותף ל-`ty-condition-help.js`, שמוחק אותו כשהלקוח סוגר.
     שני הקבצים חייבים לומר את אותה מחרוזת. */
  const NOTICE_KEY = 'tyCondNotice';
  const NOTICE_MS = 8000;

  const COND_NEW = 'חדש';
  const COND_ACTIVE = 'Active New';

  /** כל הבוררים בדף, כולל כאלה שנוספו אחרי רינדור מחדש. */
  function pickers() {
    return document.querySelectorAll('variant-selects');
  }

  function readMap(root) {
    const el = root.querySelector('script[data-ty-cond-map]');
    if (!el) return null;
    try {
      return { avail: JSON.parse(el.textContent), mode: el.dataset.tyCondMode || 'auto' };
    } catch (e) {
      /* 🔴 מפה פגומה אינה סיבה לשבור את הדף. יוצאים בשקט, והבורר
         ממשיך להתנהג כמו בורר רגיל. */
      return null;
    }
  }

  /** ערכי כל האופציות שאינן «מצב», בסדר הרינדור. */
  function restValues(root) {
    const out = [];
    root.querySelectorAll('fieldset.product-form__input').forEach((fs) => {
      if (fs.hasAttribute('data-ty-cond')) return;
      const checked = fs.querySelector('input:checked');
      if (checked) out.push(checked.value);
    });
    root.querySelectorAll('select.select__select').forEach((sel) => {
      const fs = sel.closest('[data-ty-cond]');
      if (fs) return;
      out.push(sel.value);
    });
    return out;
  }

  function decide(avail, mode, rest) {
    const key = (cond) => rest.concat(cond).join('||');
    if (mode === 'new') return COND_NEW;
    if (mode === 'active') return COND_ACTIVE;
    /* auto: «חדש» קודם, ורק אם אין ממנו מלאי עוברים לשני. */
    if (avail[key(COND_NEW)] === true) return COND_NEW;
    if (avail[key(COND_ACTIVE)] === true) return COND_ACTIVE;
    return null;   /* לשניהם אין מלאי. משאירים כמו שהוא, ושופיפיי תציג «אזל». */
  }

  function sync(root) {
    const fs = root.querySelector('[data-ty-cond]');
    if (!fs) return;
    const data = readMap(root);
    if (!data) return;

    const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
    if (!inputs.length) return;
    const current = inputs.find((i) => i.checked);
    const want = decide(data.avail, data.mode, restValues(root));
    if (!want) return;
    if (current && current.value === want) return;   /* כבר נכון. בלי לחיצה, בלי בקשה. */

    const target = inputs.find((i) => i.value === want);
    if (!target || target === current) return;

    /* ── ההודעה במעבר, 21.09.2026 ──
       🔴 **רק מ«חדש» ל-«Active New»**, ורק כשזה באמת מעבר. הכיוון ההפוך
          אינו דורש הסבר, ופתיחה בטעינת הדף הייתה מטרידה בלי סיבה.
       🔴 הדגל נשמר ב-sessionStorage ולא במשתנה, כי בין הלחיצה לבין
          הרינדור מחדש ה-DOM מוחלף והמשתנה על האלמנט נעלם איתו. */
    if (current && current.value === COND_NEW && want === COND_ACTIVE) {
      try { sessionStorage.setItem(NOTICE_KEY, String(Date.now())); } catch (e) { /* מצב פרטי */ }
    }

    /* ⚠️ מסמנים שאנחנו מקליקים, כדי שהמאזין שלנו לא יגיב ללחיצה של
       עצמו וייכנס ללולאה. הדגל יורד אחרי שהשינוי עובד. */
    root.__tyCondBusy = true;
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
    setTimeout(() => { root.__tyCondBusy = false; }, 0);
  }

  /**
   * פותח את הבועית עם שורת «שים לב», אם נרשם מעבר ברגעים האחרונים.
   *
   * 🔴 **אינו מוחק את הדגל בהצלחה, וזו כל הנקודה.** החלפת ווריאנט מרנדרת
   *    מחדש **פעמיים**: פעם על הנפח שהלקוח לחץ, ופעם על המצב שאנחנו
   *    מקליקים אחריו. ‏`product-info.js` מחליף את שורת האייקונים **אחרי**
   *    הבורר, ולכן בועית שנפתחת על הרינדור הראשון נמחקת מיד אחר כך.
   *    ⟹ הדגל נשאר, והבועית נפתחת מחדש בכל הופעה של הסימן, עד שהלקוח
   *    סוגר אותה או עד שחלון הזמן נגמר.
   *
   * 🔴 מחפש בכל המסמך ולא בתוך הבורר. הסימן יושב בשורת האייקונים, שהיא
   *    אח של הבורר ולא צאצא שלו.
   */
  function showPendingNotice() {
    let stamp = null;
    try { stamp = sessionStorage.getItem(NOTICE_KEY); } catch (e) { return; }
    if (!stamp) return;
    /* ⚠️ חלון זמן, כדי שדגל שנשאר מטעינה קודמת לא יקפיץ בועית משום מקום. */
    if (Date.now() - Number(stamp) > NOTICE_MS) {
      try { sessionStorage.removeItem(NOTICE_KEY); } catch (e) { /* */ }
      return;
    }
    /* ty-cond-notice-auto >>> */ if (typeof window.tyCondNoticeShow === 'function') { window.tyCondNoticeShow(document.querySelector('.product__info-container') || document); return; } /* <<< ty-cond-notice-auto */
    const scope = document.querySelector('.product__info-container') || document;
    const wrap = scope.querySelector('[data-ty-condhelp]');
    if (!wrap) return;
    const notice = wrap.querySelector('.ty-condhelp__notice');
    if (!notice) return;
    notice.hidden = false;
    if (typeof window.tyCondHelpOpen === 'function') window.tyCondHelpOpen(wrap);
  }

  function onChange(event) {
    const root = event.target.closest && event.target.closest('variant-selects');
    if (!root || root.__tyCondBusy) return;
    /* הבורר של «מצב» מוסתר, ולכן שינוי בו יכול להגיע רק מאיתנו. */
    if (event.target.closest('[data-ty-cond]')) return;
    /* מריצים אחרי שהתמה סיימה לעדכן את הסימון. */
    setTimeout(() => sync(root), 0);
  }

  /**
   * 🔴 `product-info.js` נטען דרך `lazy-script`, ולכן בטעינת הדף עדיין אין
   *    מי שמאזין ל-`optionValueSelectionChange`. הקלקה לפני כן מסמנת את
   *    הרדיו ו**אינה מרעננת כלום**: המחיר, המק"ט, הכפתור ושורת האייקונים
   *    נשארים על הווריאנט שהשרת בחר, והבורר מתחיל לשקר.
   *    נמדד 21.09.2026 בדפדפן: **אפס בקשות `section_id`** אחרי ההקלקה.
   * ⟹ מחכים שהרכיב יוגדר. אם `lazy-script` לעולם אינו נטען, לא נוגעים
   *    בבחירת השרת, וזה הכיוון הבטוח.
   */
  /* ty-cond-deeplink >>> */
  /* 13.10.2026. קישור ישיר ל«חדש» שאזל, בטלפון, נשאר על «אזל מהמלאי».
     🔴 ה-callback של product-info קורא ל-share-button.init() ול-product-form.init().
        share.js נטען רק כשהרכיב או הבורר נראים במסך, ובטלפון הם מתחת לקיפול.
        ⟹ ההקלקה שלנו הגיעה לפני ש-share-button הוגדר, ה-callback נפל בשקט,
        והכתובת, המק"ט והכפתור נשארו על «חדש» שאזל. נמדד בדפדפן.
     ⟹ מחכים גם להם, וטוענים אותם מיד (loadScript אינו טוען פעמיים).
     ⚠️ לא הוגדרו תוך 6 שניות ⟵ לא נוגעים. בחירת השרת נשארת, כמו כש-product-info לא נטען.
     במחשב הם כבר מוגדרים, ולכן שם אין שום שינוי. */
  const TY_DEEP_TAGS = ['share-button', 'product-form', 'pickup-availability'];
  const TY_DEEP_MS = 6000;
  function tyDeepWait(fn) {
    return () => {
      const pi = document.querySelector('product-info');
      const missing = TY_DEEP_TAGS.filter((t) => pi && pi.querySelector(t) && !customElements.get(t));
      if (!missing.length) { fn(); return; }
      document.querySelectorAll('lazy-script[data-watch-element]').forEach((ls) => {
        const watch = (ls.getAttribute('data-watch-element') || '').split(',').map((s) => s.trim());
        if (missing.some((t) => watch.includes(t)) && typeof ls.loadScript === 'function') ls.loadScript();
      });
      let late = false;
      const timer = setTimeout(() => { late = true; }, TY_DEEP_MS);
      Promise.all(missing.map((t) => customElements.whenDefined(t))).then(() => {
        if (late) return;
        clearTimeout(timer);
        setTimeout(fn, 0);
      });
    };
  }
  /* <<< ty-cond-deeplink */
  function whenReady(fn) {
    /* ty-cond-deeplink >>> */ fn = tyDeepWait(fn); /* <<< ty-cond-deeplink */
    if (customElements.get('product-info')) { fn(); return; }
    customElements.whenDefined('product-info').then(() => setTimeout(fn, 0));
  }

  function boot() {
    whenReady(() => {
      pickers().forEach((root) => sync(root));
      showPendingNotice();
    });
  }

  document.addEventListener('change', onChange, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* 🔴 `product-info.js` מחליף את תוכן הבורר בכל שינוי ווריאנט, והמאזין
     על document שורד את זה. מה שלא שורד הוא הסנכרון ההתחלתי על מרקאפ
     חדש, ולכן צופים בשינויי DOM ומסנכרנים שוב. */
  const mo = new MutationObserver((records) => {
    let sawHelp = false;
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue;
        const has = (sel) => (n.matches && n.matches(sel)) || (n.querySelector && n.querySelector(sel));
        if (n.matches && n.matches('variant-selects')) sync(n);
        else if (n.querySelector && n.querySelector('variant-selects')) pickers().forEach((root) => sync(root));
        /* 🔴 שורת האייקונים מוחלפת **אחרי** הבורר, ולכן ההודעה נתלית בה
           ולא בו. תלייה בבורר פתחה בועית שנמחקה באותו רגע. */
        if (has('[data-ty-condhelp]')) sawHelp = true;
      }
    }
    if (sawHelp) showPendingNotice();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
